import { useRouter } from '@tanstack/react-router';
import {
  Banner,
  Button,
  Link,
  PageHeader,
  PageHeaderActions,
  PageHeaderIconTile,
  PageHeaderLabel,
  PageHeaderTabs,
  PageHeaderTitle,
  TabNav,
  TabNavList,
  TabNavTab,
} from '@customerio/pluma-components/react';
import { objectTypeIcon, type ObjectType } from '@/services/db/object-type/entity';
import type { WorkspaceEnvironment } from '@/services/db/environment/entity';
import { ObjectExport } from './object-export';
import { AddObjectDropdown } from './add-object-dropdown';
import { useAddRelationshipsModal } from './add-relationships-modal';

type ObjectTypeHeaderProps = {
  objectType: ObjectType;
  workspaceId: number;
  environment: WorkspaceEnvironment['environment'];
  activeTab: 'guide' | 'list';
  canViewSettings: boolean;
  canCreate: boolean;
  canExport: boolean;
  canCreateRelationships: boolean;
  displayedAttributes: readonly string[];
  exportFilters: string | null;
  isExportBroaderThanList: boolean;
  isListLoaded: boolean;
};

export function ObjectTypeHeader(props: ObjectTypeHeaderProps) {
  const router = useRouter();
  const relationshipsModal = useAddRelationshipsModal();
  const showTabs = props.objectType.has_non_ui_objects !== true || !props.objectType.enabled;
  const manageHref = router.buildLocation({
    to: '/workspaces/$workspaceId/settings/objects/edit/$objectType',
    params: { workspaceId: props.workspaceId, objectType: props.objectType.id },
  }).href;
  const guideHref = router.buildLocation({
    to: '/workspaces/$workspaceId/objects/$slug/guide',
    params: { workspaceId: props.workspaceId, slug: props.objectType.slug },
  }).href;
  const listHref = router.buildLocation({
    to: '/workspaces/$workspaceId/objects/$slug',
    params: { workspaceId: props.workspaceId, slug: props.objectType.slug },
  }).href;

  return (
    <PageHeader>
      <PageHeaderIconTile name={objectTypeIcon(props.objectType)} />
      {!props.objectType.enabled ? <PageHeaderLabel>Disabled</PageHeaderLabel> : null}
      <PageHeaderTitle>{props.objectType.name}</PageHeaderTitle>
      <PageHeaderActions>
        <ObjectExport
          objectType={props.objectType}
          displayedAttributes={props.displayedAttributes}
          filters={props.exportFilters}
          isBroaderThanList={props.isExportBroaderThanList}
          isDisabled={!props.canExport || !props.isListLoaded}
        />
        <Button icon="manage" href={manageHref} isDisabled={!props.canViewSettings}>
          Manage
        </Button>
        <AddObjectDropdown
          objectType={props.objectType}
          workspaceId={props.workspaceId}
          canCreate={props.canCreate}
          canCreateRelationships={props.canCreateRelationships}
          onAddRelationships={() =>
            relationshipsModal.open(
              { objectType: props.objectType, workspaceId: props.workspaceId },
              { shouldCloseOnUnmount: true },
            )
          }
        />
      </PageHeaderActions>
      {showTabs ? (
        <PageHeaderTabs>
          {!props.objectType.enabled ? (
            <Banner variant="information">
              <Link variant="secondary" href="/settings/billing/available-plans/journeys">
                Your plan
              </Link>{' '}
              allows a maximum of {props.environment?.max_allowed_object_types ?? 0} object types.{' '}
              {props.objectType.name} is disabled and cannot be used in Segments or Messages.
            </Banner>
          ) : null}
          {props.objectType.has_non_ui_objects !== true ? (
            <TabNav aria-label={`${props.objectType.name} pages`}>
              <TabNavList>
                <TabNavTab href={guideHref} isActive={props.activeTab === 'guide'}>
                  Setup guide
                </TabNavTab>
                <TabNavTab href={listHref} isActive={props.activeTab === 'list'}>
                  {props.objectType.name}
                </TabNavTab>
              </TabNavList>
            </TabNav>
          ) : null}
        </PageHeaderTabs>
      ) : null}
    </PageHeader>
  );
}
